
import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { request } from "app/network";

interface TermData {
    title: string;
    content: string;
    version: string;
    effectiveDate?: string;
}

// URL 타입과 실제 DB의 TermsType 매핑
// 실제 DB에 저장된 TERMS_TYPE 공통 코드 값이어야 함
const TERM_CODE_MAP: { [key: string]: string } = {
    privacy: "TERMS_PRIVACY",
    service: "TERMS_SERVICE",
    marketing: "TERMS_MARKETING",
};

interface PublicTermScreenProps {
    initialType?: string;
}

const PublicTermScreen = ({ initialType }: PublicTermScreenProps = {}) => {
    const { id, type } = useParams<{ id?: string; type?: string }>();
    const [term, setTerm] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTerm = async () => {
            try {
                setLoading(true);
                setError(null); // Clear previous errors
                let termData = null;

                const targetType = initialType || type;

                if (id) {
                    // ID로 직접 조회
                    const res = await request("LegalTerms.getById", {
                        pathParams: { id },
                    });
                    if (res && res.data) {
                        termData = res.data;
                    }
                } else if (targetType) {
                    // 타입으로 활성 약관 조회 (active 상태인 것 중 최신 버전)
                    const termCode = TERM_CODE_MAP[targetType.toLowerCase()];

                    if (!termCode) {
                        throw new Error(`잘못된 약관 유형입니다: ${targetType}`);
                    }

                    // 모든 활성 약관 목록 조회 (웹 전용 - content 포함)
                    const res = await request("LegalTerms.activeWithContent");

                    if (res && res.data) {
                        const allActiveTerms = res.data;

                        // 1. 요청된 타입과 일치하고
                        // 2. (이미 서버에서 active=true로 필터링됨)
                        // 3. 시행일(EffectiveDate) 내림차순, 버전(Version) 내림차순 정렬
                        const matchingTerms = allActiveTerms.filter((term: any) => term.termsType === termCode);

                        if (matchingTerms.length > 0) {
                            matchingTerms.sort((a: any, b: any) => {
                                // 시행일 비교 (최신순)
                                const dateA = new Date(a.effectiveDate).getTime();
                                const dateB = new Date(b.effectiveDate).getTime();
                                if (dateA !== dateB) return dateB - dateA;

                                // 버전 비교 (문자열 비교지만 일반적인 버전 형식 가정)
                                return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' });
                            });

                            // 가장 최신 약관 선택
                            termData = matchingTerms[0];
                        } else {
                            // 디버깅을 위해 가용한 모든 약관 타입 수집
                            const availableTypes = Array.from(new Set(allActiveTerms.map((t: any) => t.termsType))).join(", ");
                            throw new Error(`해당 유형(${termCode})의 활성 약관을 찾을 수 없습니다. (데이터베이스에 존재하는 유형: ${availableTypes || "없음"})`);
                        }
                    }
                }

                if (termData) {
                    setTerm(termData);
                } else {
                    if (!error) throw new Error("약관을 불러올 수 없습니다.");
                }
            } catch (err: any) {
                console.error("약관 조회 실패:", err);
                setError(err.message || "약관을 불러오는 중 오류가 발생했습니다.");
            } finally {
                setLoading(false);
            }
        };

        fetchTerm();
    }, [id, type, initialType]);

    if (loading) {
        return (
            <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                bgcolor="#f8f9fa"
            >
                <CircularProgress color="info" />
            </MDBox>
        );
    }

    if (error) {
        return (
            <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                bgcolor="#f8f9fa"
                p={3}
            >
                <MDTypography variant="h6" color="error">
                    {error}
                </MDTypography>
            </MDBox>
        );
    }

    if (!term) return null;

    return (
        <MDBox
            sx={{
                width: "100%",
                margin: "0",

            }}
        >
            {/* Title, Version, and Date removed per user request */}
            <MDBox
                sx={{
                    fontFamily: '"Pretendard", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    color: "#344767",
                    "& img": {
                        maxWidth: "100%",
                        height: "auto",
                    },
                    "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        marginBottom: "1rem",
                    },
                    "& th, & td": {
                        border: "1px solid #ddd",
                        padding: "8px",
                    },
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                        fontWeight: 700,
                        margin: "1.5em 0 0.8em 0",
                        color: "#1a1a1a",
                    },
                    "& p": {
                        margin: "0.8em 0",
                    },
                    "& ul, & ol": {
                        paddingLeft: "24px",
                        margin: "1em 0",
                        listStyleType: "disc",
                    },
                    "& li": {
                        display: "list-item",
                        marginBottom: "0.5em",
                    },
                    "& li::marker": {
                        color: "#3b82f6",
                    }
                }}
                dangerouslySetInnerHTML={{ __html: term.content }}
            />
        </MDBox>
    );
};

export default PublicTermScreen;
